import React, { useCallback, useMemo } from 'react';
import Typography from '@material-ui/core/Typography';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { BidFileSearchResult } from '../../api-client/autogenerated';
import { Tooltip } from '@material-ui/core';
import { downloadFileById, openInNewTab } from '../../scripts/utils';

const useStyles = makeStyles(() =>
  createStyles({
    documentItem: {
      padding: 16,
      paddingLeft: 47,
      border: '1px solid transparent',
      cursor: 'pointer',
      position: 'relative',
      '&:hover': {
        backgroundColor: '#F5F5F5',
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        left: '24px',
        top: -2,
        bottom: 0,
        width: '2px',
        backgroundColor: '#0947B9',
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        left: '24px',
        top: 24,
        width: '12px',
        height: '2px',
        backgroundColor: '#0947B9',
      },
      '&:last-child': {
        '&::before': {
          height: 28,
        },
      },
    },
  }),
);

interface Props {
  bidFile: BidFileSearchResult;
  searchTerm: string;
}

/**
 * A bid setup file laid out like a document result: the group above supplies the type, so the row
 * carries the name and nothing else, the way a document row with no text match does.
 *
 * A document row opens its document and lists its matched files beneath. These files have no
 * document behind them - the Project Manual and Bid Drawings logs are the bid setup's files - and
 * the file is the record, so the row opens it directly rather than repeating its own name as an
 * attachment.
 */
export default function BidFileSearchItem(props: Props) {
  const { bidFile, searchTerm } = props;
  const classes = useStyles();

  const isPdf = bidFile.name.toLowerCase().endsWith('.pdf');

  const handleSelect = useCallback(() => {
    if (isPdf) {
      openInNewTab(bidFile.id, false, searchTerm);
    } else {
      downloadFileById(bidFile.id, bidFile.name);
    }
  }, [bidFile, isPdf, searchTerm]);

  const content = useMemo(
    () => (
      <div key={bidFile.id} onClick={handleSelect} className={classes.documentItem}>
        <Typography style={{ fontSize: '16px', fontWeight: 600 }}>{bidFile.name}</Typography>
      </div>
    ),
    [bidFile, classes, handleSelect],
  );

  return (
    <Tooltip arrow title={isPdf ? 'View File' : 'Click to download file'} placement="right-start">
      {content}
    </Tooltip>
  );
}
